import {
  EditableRowsProvider,
  EditableSettingRow,
  SettingRowsCard,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import { useSettingsSupplier } from "@/app/Suppliers/Utils/useSettingsSupplier";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { updateSupplierMutation } from "@/app/Suppliers/GraphQL/supplierMutations";
import { ClosedDatesForm } from "@/app/Suppliers/Components/ClosedDatesForm";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import { IllustrationCalendar } from "@/assets/Illustrations";
import {
  fromOpenDaysSchema,
  toOpenDaysSchema,
} from "@/app/Suppliers/Utils/mapClosedDays";
import { sortOpenHours } from "@/app/Offices/Utils/sortOpenHours";
import { summariseOpenHours } from "@/app/Offices/Utils/summariseOpenHours";
import { OpenHoursInput } from "@/app/Offices/Components/OpenHoursInput";
import { redirect } from "@tanstack/react-router";
import { supplierOfficeOpenHoursList } from "@/app/Offices/GraphQL/supplierOfficeOpenHoursList";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { OfficeDrawerLink } from "@/app/Offices/Components/Drawer/OfficeDrawerLink";

export function SettingOpenHoursScreen() {
  const { supplier } = useSettingsSupplier();
  const { mutateAsync } = useGqlMutation(updateSupplierMutation);

  if (!supplier) {
    throw redirect({
      from: "/$tenant/$orgId/supplier/{-$supplierId}",
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings",
      params: (prev) => prev,
      search: (prev) => prev,
    });
  }

  const { data } = useGqlQuery(supplierOfficeOpenHoursList, {
    supplierId: supplier.id,
  });

  const customPickupHours = data?.supplierOffices.data.filter((office) => {
    return office.departure_hours !== null;
  });

  const customDropoffHours = data?.supplierOffices.data.filter((office) => {
    return office.delivery_hours !== null;
  });

  const closedDates = fromOpenDaysSchema(supplier.closed_dates);

  return (
    <EditableRowsProvider>
      <div className="space-y-8">
        <SettingRowsCard
          title="Departure hours"
          description="Specify the hours when the offices are open for pickup"
        >
          <EditableSettingRow
            label="Departure hours"
            value={summariseOpenHours(supplier.departure_hours)}
            editClassName="max-w-none"
            initialValues={{
              departure_hours: sortOpenHours(supplier.departure_hours),
            }}
            onSave={(values) => {
              return mutateAsync({
                id: supplier.id,
                input: values,
              });
            }}
          >
            {customPickupHours?.length ? (
              <WarningBanner className="col-span-full mb-3">
                <p>
                  {customPickupHours.length} offices have custom pickup hours
                </p>
                <ul className="mt-3 list-inside list-disc">
                  {customPickupHours?.map((office) => {
                    return (
                      <li key={office.id}>
                        <OfficeDrawerLink officeId={office.id}>
                          {office.name}
                        </OfficeDrawerLink>
                      </li>
                    );
                  })}
                </ul>
              </WarningBanner>
            ) : null}

            <OpenHoursInput
              prefix="departure_hours"
              className="col-span-full"
              label="Departure Hours"
            />
          </EditableSettingRow>
        </SettingRowsCard>

        <SettingRowsCard
          title="Delivery hours"
          description="Specify the hours when the offices are open for drop-off"
        >
          <EditableSettingRow
            label="Delivery hours"
            value={summariseOpenHours(supplier.delivery_hours)}
            editClassName="max-w-none"
            initialValues={{
              delivery_hours: sortOpenHours(supplier.delivery_hours),
            }}
            onSave={(values) => {
              return mutateAsync({
                id: supplier.id,
                input: values,
              });
            }}
          >
            {customDropoffHours?.length ? (
              <WarningBanner className="col-span-full mb-3">
                <p>
                  {customDropoffHours.length} offices have custom delivery hours
                </p>
                <ul className="mt-3 list-inside list-disc">
                  {customDropoffHours?.map((office) => {
                    return (
                      <li key={office.id}>
                        <OfficeDrawerLink officeId={office.id}>
                          {office.name}
                        </OfficeDrawerLink>
                      </li>
                    );
                  })}
                </ul>
              </WarningBanner>
            ) : null}

            <OpenHoursInput
              prefix="delivery_hours"
              className="col-span-full"
              label="Delivery Hours"
            />
          </EditableSettingRow>
        </SettingRowsCard>

        <SettingRowsCard
          title="Closed dates"
          description="Specify the dates when all offices are closed"
        >
          <EditableSettingRow
            label="Closed dates"
            value={closedDates.length ? `${closedDates.length} dates` : null}
            editClassName="max-w-2xl"
            initialValues={{
              closed_dates: closedDates,
            }}
            onSave={async (values) => {
              const foo = toOpenDaysSchema(values.closed_dates);
              await mutateAsync({
                id: supplier.id,
                input: {
                  closed_dates: foo,
                },
              });
            }}
          >
            <ClosedDatesForm
              emptyState={
                <EmptyState
                  Icon={IllustrationCalendar}
                  header="No closed dates"
                  subheader="Add date(s) you want to close all offices on"
                />
              }
            />
          </EditableSettingRow>
        </SettingRowsCard>
      </div>
    </EditableRowsProvider>
  );
}
